/**
 * Request Utilities
 * 
 * Utility functions for building and normalizing API URLs used by the API client.
 * Handles endpoint normalization, URL construction, and duplicate path segment
 * removal to ensure clean API endpoint URLs.
 */

/**
 * Ensures an endpoint string has a leading slash.
 * Returns '/' if the endpoint is empty or falsy.
 * @param {string} endpoint - The API endpoint path
 * @returns {string} Endpoint with leading slash
 */
export function ensureLeadingSlash(endpoint) {
  if (!endpoint) {return '/';}
  return endpoint.startsWith('/') ? endpoint : '/' + endpoint;
}

/**
 * Constructs a complete API URL from a base URL and endpoint.
 * Removes duplicate path segments and ensures clean URL formatting.
 * Handles special cases where duplicate prefixes occur.
 * @param {string} base - The base API URL
 * @param {string} endpoint - The API endpoint path
 * @returns {string} Complete normalized API URL
 */
export function buildUrl(base, endpoint) {
  let ep = ensureLeadingSlash(endpoint || '');
  const cleanBase = (base || '').replace(/\/+$/g, '');

  try {
    const dupPrefix = '/auth-service/api';
    if (cleanBase.endsWith(dupPrefix) && ep.startsWith(dupPrefix)) {
      ep = ep.replace(new RegExp('^' + dupPrefix), '');
    }
  } catch (e) {
  }

  const raw = `${cleanBase}${ep}`;

  // Ensure HTTPS for production domains (security: prevent mixed content)
  let finalUrl = raw
    .replace(/\/api(\/api)+/g, '/api')
    .replace(/(\/auth-service\/api)+(\/auth-service\/api)*/g, '/auth-service/api');
  
  // Force HTTPS for gateway.ismartghana.com and invoice service IPs
  if ((finalUrl.includes('gateway.ismartghana.com') || finalUrl.includes('3.22.175.96')) && finalUrl.startsWith('http://')) {
    finalUrl = finalUrl.replace('http://', 'https://');
  }
  
  return finalUrl;
}

/**
 * Constructs the refresh token URL from base URL and refresh endpoint.
 * Uses the default refresh endpoint if none is provided.
 * @param {string} base - The base API URL
 * @param {string} refreshEndpoint - Optional refresh endpoint path (defaults to '/auth/token/refresh')
 * @returns {string} Complete refresh token URL
 */
export function buildRefreshUrl(base, refreshEndpoint) {
  return buildUrl(base, refreshEndpoint || '/auth/token/refresh');
}

export default { ensureLeadingSlash, buildUrl, buildRefreshUrl };
